import type { ProjectTask } from '@/types/odoo';

/**
 * Pure helpers for the dashboard read-only planning week grid (Fase 3a).
 * The grid is derived entirely from detail.projectTasks (already loaded) — no
 * extra fetch. Dates use local components (never toISOString) per the project's
 * CET/CEST date gotcha.
 */

const pad = (n: number): string => String(n).padStart(2, '0');

export const dateKey = (d: Date): string => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

export function mondayOf(d: Date): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = r.getDay(); // 0=Sun … 6=Sat
  r.setDate(r.getDate() + (day === 0 ? -6 : 1 - day));
  return r;
}

export function addDays(d: Date, n: number): Date {
  const r = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  r.setDate(r.getDate() + n);
  return r;
}

/** The 7 days (Mon–Sun) of the week starting at weekStart. Sat/Sun are included
 *  so a weekend-scheduled task is never silently dropped from the grid. */
export function weekdays(weekStart: Date): Date[] {
  return [0, 1, 2, 3, 4, 5, 6].map((i) => addDays(weekStart, i));
}

const DAY_LABELS = ['Ma', 'Di', 'Wo', 'Do', 'Vr', 'Za', 'Zo'];

export function dayHeader(d: Date): { label: string; date: string } {
  const idx = (d.getDay() + 6) % 7; // Mon=0
  return { label: DAY_LABELS[idx], date: `${pad(d.getDate())}/${pad(d.getMonth() + 1)}` };
}

/** Planning tasks (non-collector, dated); scoped to a fase via parent_id when given. */
export function planningTasksForFase(tasks: ProjectTask[], faseId: number | null): ProjectTask[] {
  return tasks.filter((t) => {
    if (t.x_studio_collector) return false;
    if (!t.date_deadline) return false;
    if (faseId !== null) {
      const parentId = Array.isArray(t.parent_id) ? t.parent_id[0] : null;
      if (parentId !== faseId) return false;
    }
    return true;
  });
}

/** Unplanned tasks (non-collector, NOT dated); scoped to a fase via parent_id when
 *  given. The inverse of planningTasksForFase — these are the "nog in te plannen"
 *  backlog items the Inplannen modal lets you drag onto the calendar. */
export function unplannedTasksForFase(tasks: ProjectTask[], faseId: number | null): ProjectTask[] {
  return tasks.filter((t) => {
    if (t.x_studio_collector) return false;
    if (t.date_deadline) return false;
    if (faseId !== null) {
      const parentId = Array.isArray(t.parent_id) ? t.parent_id[0] : null;
      if (parentId !== faseId) return false;
    }
    return true;
  });
}

/** Monday of the week containing the most relevant task (nearest future, else latest). */
export function defaultWeekStart(tasks: ProjectTask[], today: Date = new Date()): Date {
  const dated = tasks
    .filter((t) => t.date_deadline)
    .map((t) => String(t.date_deadline).slice(0, 10))
    .sort();
  if (dated.length === 0) return mondayOf(today);
  const todayStr = dateKey(today);
  const pick = dated.find((d) => d >= todayStr) ?? dated[dated.length - 1];
  const [y, m, d] = pick.split('-').map(Number);
  return mondayOf(new Date(y, m - 1, d));
}

/** Monday of the week containing the fase's EARLIEST task (its start week), so a
 *  multi-week fase opens at the beginning; the jump hints lead forward from there. */
export function faseStartWeek(tasks: ProjectTask[], today: Date = new Date()): Date {
  const dated = tasks
    .filter((t) => t.date_deadline)
    .map((t) => String(t.date_deadline).slice(0, 10))
    .sort();
  if (dated.length === 0) return mondayOf(today);
  const [y, m, d] = dated[0].split('-').map(Number);
  return mondayOf(new Date(y, m - 1, d));
}

export interface FaseWeek {
  weekStart: Date;
  count: number;
}

/** All weeks (by Monday) that contain fase tasks, with per-week task counts,
 *  sorted chronologically. Powers the "deze fase loopt over meerdere weken"
 *  timeline so a multi-week fase is never silently truncated; the grid marks the
 *  currently-visible week as "you are here". */
export function faseTaskWeeks(tasks: ProjectTask[]): FaseWeek[] {
  const byWeek = new Map<string, number>();
  for (const t of tasks) {
    if (!t.date_deadline) continue;
    const [y, m, d] = String(t.date_deadline).slice(0, 10).split('-').map(Number);
    const key = dateKey(mondayOf(new Date(y, m - 1, d)));
    byWeek.set(key, (byWeek.get(key) ?? 0) + 1);
  }
  return [...byWeek.entries()]
    .map(([key, count]) => {
      const [y, m, d] = key.split('-').map(Number);
      return { weekStart: new Date(y, m - 1, d), count };
    })
    .sort((a, b) => a.weekStart.getTime() - b.weekStart.getTime());
}

export interface InstallerRow {
  installerId: number | null; // null = unassigned
  name: string;
  byDay: Map<string, ProjectTask[]>; // dateKey → tasks
}

/** Group week-scoped tasks into installer rows × day cells (a task with N
 *  installers appears in N rows; unassigned tasks go to a trailing row). */
export function buildInstallerRows(
  tasks: ProjectTask[],
  week: Date[],
  installerNames: Record<string, string>,
): InstallerRow[] {
  const weekKeys = new Set(week.map(dateKey));
  const rows = new Map<number | 'none', InstallerRow>();
  const ensure = (id: number | null, name: string): InstallerRow => {
    const key = id ?? 'none';
    let row = rows.get(key);
    if (!row) {
      row = { installerId: id, name, byDay: new Map() };
      rows.set(key, row);
    }
    return row;
  };
  for (const t of tasks) {
    const dk = String(t.date_deadline).slice(0, 10);
    if (!weekKeys.has(dk)) continue;
    const installers = Array.isArray(t.x_studio_installateur) ? t.x_studio_installateur : [];
    if (installers.length === 0) {
      ensure(null, 'Niet toegewezen').byDay.set(dk, [...(rows.get('none')?.byDay.get(dk) ?? []), t]);
    } else {
      for (const iid of installers) {
        const row = ensure(iid, installerNames[String(iid)] ?? `#${iid}`);
        row.byDay.set(dk, [...(row.byDay.get(dk) ?? []), t]);
      }
    }
  }
  return [...rows.values()].sort((a, b) => {
    if (a.installerId === null) return 1; // unassigned last
    if (b.installerId === null) return -1;
    return a.name.localeCompare(b.name);
  });
}
